import { Trans, useLingui } from "@lingui/react/macro";
import { FileSpreadsheet, Loader2, Upload } from "lucide-react";
import type * as React from "react";

import { cn } from "@ando/ui";

import {
  SPREADSHEET_FILE_ACCEPT,
  SPREADSHEET_MAX_UPLOAD_BYTES,
} from "../../wizards/spreadsheetSpec";
import { formatBytes } from "./types";

interface SpreadsheetFileDropzoneProps {
  readonly file: File | null;
  readonly busy: boolean;
  readonly dragOver: boolean;
  readonly fileInputRef: React.RefObject<HTMLInputElement | null>;
  readonly onDragOver: (event: React.DragEvent<HTMLElement>) => void;
  readonly onDragLeave: (event: React.DragEvent<HTMLElement>) => void;
  readonly onDrop: (event: React.DragEvent<HTMLElement>) => void;
  readonly onPick: () => void;
  readonly onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readonly surface?: "workspace" | "console";
}

/** The same spreadsheet picker in Workspace and Console; only surface tokens differ. */
export function SpreadsheetFileDropzone({
  file,
  busy,
  dragOver,
  fileInputRef,
  onDragOver,
  onDragLeave,
  onDrop,
  onPick,
  onFileChange,
  surface = "workspace",
}: SpreadsheetFileDropzoneProps): React.ReactElement {
  const { t } = useLingui();
  const consoleSurface = surface === "console";
  return (
    <div>
      <input
        ref={fileInputRef}
        name="file"
        type="file"
        accept={SPREADSHEET_FILE_ACCEPT}
        className="sr-only"
        onChange={onFileChange}
        disabled={busy}
        aria-label={t`Seleziona un file Excel o CSV`}
      />
      <button
        type="button"
        aria-label={t`Trascina qui un file o seleziona dal disco`}
        onClick={onPick}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        disabled={busy}
        data-testid="spreadsheet-dropzone"
        className={cn(
          "flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-8 transition-colors",
          consoleSurface
            ? "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--con-focus)]"
            : "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
          consoleSurface
            ? dragOver
              ? "border-[color:var(--con-accent)] bg-[color:var(--con-accent-soft)]"
              : file
                ? "border-[color:var(--con-accent)] bg-[color:var(--con-accent-soft)]"
                : "border-[color:var(--con-border-strong)] hover:border-[color:var(--con-accent)] hover:bg-[color:var(--con-accent-soft)]"
            : dragOver
              ? "border-ando-orange bg-ando-orange-soft/40"
              : file
                ? "border-ando-orange/50 bg-ando-orange-soft/20"
                : "border-hairline bg-white/40 hover:border-ando-orange/40 hover:bg-white/70",
        )}
      >
        {busy ? (
          <>
            <Loader2
              className={cn(
                "h-7 w-7 animate-spin",
                consoleSurface ? "text-[color:var(--con-accent)]" : "text-ando-orange",
              )}
              aria-hidden
            />
            <p
              className={cn(
                "text-[12.5px]",
                consoleSurface ? "text-[color:var(--con-on-surface-muted)]" : "text-ink-2",
              )}
            >
              <Trans>Analizzo il foglio…</Trans>
            </p>
          </>
        ) : file ? (
          <>
            <FileSpreadsheet
              className={cn(
                "h-7 w-7",
                consoleSurface ? "text-[color:var(--con-accent)]" : "text-ando-orange",
              )}
              aria-hidden
            />
            <div className="text-center">
              <p
                data-testid="picked-file-name"
                className={cn(
                  "font-medium text-[13px]",
                  consoleSurface ? "text-[color:var(--con-on-surface)]" : "text-ink-1",
                )}
              >
                {file.name}
              </p>
              <p
                className={cn(
                  "mt-0.5 text-[11.5px]",
                  consoleSurface ? "text-[color:var(--con-on-surface-muted)]" : "text-ink-3",
                )}
              >
                {formatBytes(file.size)}
              </p>
            </div>
            <p
              className={cn(
                "text-[11px]",
                consoleSurface ? "text-[color:var(--con-on-surface-muted)]" : "text-ink-3",
              )}
            >
              <Trans>Clicca per cambiare file</Trans>
            </p>
          </>
        ) : (
          <>
            <Upload
              className={cn(
                "h-7 w-7",
                consoleSurface ? "text-[color:var(--con-on-surface-muted)]" : "text-ink-3",
              )}
              aria-hidden
            />
            <div className="text-center">
              <p
                className={cn(
                  "font-medium text-[13px]",
                  consoleSurface ? "text-[color:var(--con-on-surface)]" : "text-ink-1",
                )}
              >
                <Trans>Trascina qui un file o seleziona</Trans>
              </p>
              <p
                className={cn(
                  "mt-0.5 text-[11.5px]",
                  consoleSurface ? "text-[color:var(--con-on-surface-muted)]" : "text-ink-3",
                )}
              >
                <Trans>
                  Formati supportati: .xlsx, .xls, .csv · max{" "}
                  {Math.round(SPREADSHEET_MAX_UPLOAD_BYTES / 1024 / 1024)} MB
                </Trans>
              </p>
            </div>
          </>
        )}
      </button>
    </div>
  );
}
